import { useRef } from 'react'

import useSWR, { useSWRConfig } from 'swr'

import type {
	GetCollectionProductsPerPageOptions,
	GetCollectionProductsPerPageReturn
} from '@lib/operations/collection'
import { getCollectionProductsPerPage } from '@lib/operations/collection'
import type { CollectionProducts, FilterValues } from '@lib/types'
import type { NonNullableExcept } from '@lib/types/utils'

type GetProductsReturn = Pick<GetCollectionProductsPerPageReturn, 'products' | 'pageInfo'>

export type UseCollectionProductsOptions = Omit<
	NonNullableExcept<GetCollectionProductsPerPageOptions, 'endCursor'>,
	'filters'
> & {
	filters: FilterValues
}

export default function useCollectionProducts(
	collectionHandle: string,
	options: UseCollectionProductsOptions,
	initialData: GetProductsReturn
) {
	const { cache } = useSWRConfig()

	const key = `/${collectionHandle}/${JSON.stringify(options)}`
	const { data, isValidating } = useSWR(key, getProducts, {
		revalidateOnMount: false,
		revalidateOnFocus: false,
		revalidateOnReconnect: true
	})

	const paginatedProducts = useRef<CollectionProducts>(initialData.products)

	// use cached data in paginatedProducts ref if it exists
	if (cache.get(key) && !isValidating) {
		const cached = cache.get(key)

		if (cached?.products) {
			paginatedProducts.current = cached.products
		}
	}

	async function getProducts(): Promise<GetProductsReturn> {
		const filtersInputs = options?.filters?.map((filter) => filter.input)

		const result = await getCollectionProductsPerPage(collectionHandle as string, {
			...options,
			filters: filtersInputs
		})

		if (!result) {
			throw new Error('Failed to fetch collection products')
		}

		const { products, pageInfo } = result

		if (options.endCursor === null) {
			paginatedProducts.current = products
		} else {
			paginatedProducts.current = [...paginatedProducts.current, ...products]
		}

		return {
			products: paginatedProducts.current,
			pageInfo
		}
	}

	return {
		products: paginatedProducts.current,
		pageInfo: data?.pageInfo ?? initialData.pageInfo,
		isLoading: isValidating
	}
}
